import { CardContent, CardShell } from '@/components/cards/common';
import { useFilterContext } from '@/context';
import { DateRange } from '@/types';
import { SupportedSocialNetwork } from '@/types/api/social-profiles';
import useFetchQuery from '@/hooks/useFetchQuery';
import { useProfilesQuery } from '@/services/revenueApi';
import { ProfilesResponse } from '@/types/api/profile';
import { PlatformIcon } from '@/components/PlatformIcon';
import { formatBigNumbers } from '@/utils/numberFormattingUtils';
import { getDateString } from '@/utils/dateUtils/dateFormatUtils';
import { Platform } from '@/types/api/platforms';

type DataType = ProfilesResponse['profiles'][number];

const FollowersCard = ({
  platform,
  className,
}: {
  platform: SupportedSocialNetwork;
  className?: string;
}) => {
  const { filters } = useFilterContext();

  const { queryResult } = useFetchQuery(useProfilesQuery, {
    enabled: filters.selectedShows.length > 0,
  });

  const isShowFilterEnabled = filters.selectedShows.length > 0;

  const dataForPlatform = queryResult.data?.profiles?.find(
    (profile) => profile.platform === platform
  );

  if (!queryResult.isLoading && !dataForPlatform) {
    return null;
  }

  return (
    <CardShell className={className} headerClassName="mb-0">
      <CardContent
        {...queryResult}
        data={dataForPlatform ?? undefined}
        renderData={(profileData: DataType) => (
          <Data
            data={profileData}
            selectedDateRange={filters.dateRange}
            selectedShow={isShowFilterEnabled ? filters.selectedShows[0] : undefined}
          />
        )}
      />
    </CardShell>
  );
};

const Data = ({
  data,
  selectedShow,
}: {
  data?: DataType;
  selectedShow: string | undefined;
  selectedDateRange: DateRange;
}) => {
  if (!data || !data.platform || selectedShow === undefined) {
    return null;
  }

  // const { to } = selectedDateRange;
  // TODO: re-enable to when we support followers over time
  const today = new Date();
  const dateString = getDateString(today.toISOString().split('T')[0]);

  // Phyllo exports 'subscribers' field for YouTube and 'followers' for other platforms
  const label =
    data.subscribers !== null && data.platform === Platform.YouTube ? 'Subscribers' : 'Followers';

  return (
    <div className="flex flex-col gap-2" data-testid="followers-card-data">
      <div className="flex flex-row items-center justify-between flex-wrap gap-1 min-h-6">
        <span className="text-textMedGrey">{label}</span>
        <span className="flex flex-row items-center gap-0.5">
          <span className="mr-1 text-textMedGrey text-xs font-light">{data.platform}</span>
          <PlatformIcon name={data.platform} />
        </span>
      </div>
      <span className="text-textDarkGrey text-2xl md:text-[24px] font-[700] font-telegraf">
        {formatBigNumbers(data.followers ?? data.subscribers ?? 0)}
      </span>
      <span className="text-textMedGrey text-xs font-normal font-inter mt-2">{dateString}</span>
    </div>
  );
};

export default FollowersCard;
